/**
 * Wattec wishlist — the engine behind every heart on the site.
 *
 * The list lives in the customer's own browser (localStorage), under the same
 * key the theme has always used, so anything a customer saved with the
 * theme's hearts before this rewrite is still there. Alongside it sits a
 * small record of when each system was saved and what it cost at the time,
 * which the wishlist page uses for "Saved 3 days ago" and price-drop notes.
 *
 * One click listener for the whole document handles every heart, including
 * hearts added later (filtered collections, the wishlist page's own cards),
 * and every heart for the same system updates together.
 *
 * Each saved build is its own entry. A system saved from its own page is
 * kept exactly as configured (variant and chosen add-ons), and a different
 * configuration of the same system is a different entry — so a customer can
 * save two builds of the same custom PC side by side, the same way the
 * floating Add to Cart bar tells builds apart for "In Cart". An entry is the
 * product handle alone ("gaming-pc") for a save with no configuration, or
 * the handle plus a short fingerprint of the build ("gaming-pc~k3j9a1").
 *
 * Other scripts use window.WattecWishlist and listen for the
 * `wattec-wishlist:change` event (detail: { list, key, handle, product,
 * action }, where handle is the same as key). The theme's older
 * `shopify-wishlist:updated` event is still fired too.
 */
(function () {
  'use strict';

  var LIST_KEY = 'shopify-wishlist';
  var META_KEY = 'wattec-wishlist-meta';
  var DELIMITER = ',';
  var ACTIVE_CLASS = 'added';
  var BUTTON_SELECTOR = '[button-wishlist]';
  var WISHLIST_URL = '/pages/wishlist';
  var KEY_JOIN = '~';

  /* ---- storage (never trusted to exist: private windows can refuse it) --- */

  function read(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }

  function write(key, value) {
    try {
      if (value === null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, value);
    } catch (e) { /* the list still works for this page view */ }
  }

  var memoryList = null;

  function getList() {
    if (memoryList) return memoryList.slice();
    var raw = read(LIST_KEY);
    if (!raw) return [];
    var seen = {};
    return raw.split(DELIMITER).filter(function (handle) {
      handle = handle.trim();
      if (!handle || seen[handle]) return false;
      seen[handle] = true;
      return true;
    });
  }

  function getMeta() {
    try { return JSON.parse(read(META_KEY)) || {}; } catch (e) { return {}; }
  }

  function saveMeta(meta) {
    write(META_KEY, Object.keys(meta).length ? JSON.stringify(meta) : null);
  }

  function saveList(list, handle, action) {
    memoryList = list.slice();
    write(LIST_KEY, list.length ? list.join(DELIMITER) : null);
    if (read(LIST_KEY) !== null || !list.length) memoryList = null;

    var meta = getMeta();
    Object.keys(meta).forEach(function (key) {
      if (list.indexOf(key) === -1) delete meta[key];
    });
    saveMeta(meta);

    refresh();
    announce(list, handle, action);
  }

  function announce(list, key, action) {
    var detail = { list: list.slice(), key: key || null, handle: key || null, product: key ? productOf(key) : null, action: action || 'set' };
    document.dispatchEvent(new CustomEvent('wattec-wishlist:change', { detail: detail }));
    document.dispatchEvent(new CustomEvent('shopify-wishlist:updated', { detail: { wishlist: list.slice() } }));
  }

  /* ---- which build an entry is ------------------------------------------ */

  /** The product an entry belongs to ("gaming-pc~k3j9a1" → "gaming-pc"). */
  function productOf(key) {
    return String(key).split(KEY_JOIN)[0];
  }

  // A short, stable fingerprint of a piece of text (FNV-1a).
  function fingerprint(text) {
    var hash = 0x811c9dc5;
    for (var i = 0; i < text.length; i++) {
      hash ^= text.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36);
  }

  /**
   * The entry for a system as configured: the handle alone when there is
   * nothing to choose (one version, no add-ons), otherwise the handle plus a
   * fingerprint of the version and every choice the customer can see — the
   * same things the floating bar compares for "In Cart". The options app's
   * price tags ("[ +£299.00 ]") are left out, so a price change never makes
   * an already-saved build look new.
   */
  function keyFor(handle, config) {
    if (!handle) return '';
    if (!config) return handle;
    var parts = [];
    if (config.vt && config.v) parts.push('v=' + config.v);
    Object.keys(config.props || {}).sort().forEach(function (name) {
      if (name.charAt(0) === '_') return;
      var value = String(config.props[name] || '')
        .replace(/\s*[\[(]\s*[+-]?\s*£?\s*[\d,.]+\s*[\])]\s*$/, '')
        .trim();
      if (value) parts.push(name + '=' + value);
    });
    return parts.length ? handle + KEY_JOIN + fingerprint(parts.join('|')) : handle;
  }

  /** Every saved entry (build) of one product. */
  function entriesFor(handle, list) {
    return (list || getList()).filter(function (key) { return productOf(key) === handle; });
  }

  /* ---- public operations ------------------------------------------------ */

  /** Whether this exact entry (or a plain product handle) is saved. */
  function has(key) {
    return getList().indexOf(key) !== -1;
  }

  /** Whether any build of this product is saved. */
  function hasProduct(handle) {
    return entriesFor(handle).length > 0;
  }

  /**
   * Saves a system. With info.config (the system as configured on its own
   * page) it is saved as that build, alongside any other builds of the same
   * system already saved. Returns the entry.
   */
  function add(handle, info) {
    if (!handle) return '';
    var config = info && info.config;
    var key = config ? keyFor(productOf(handle), config) : handle;
    var list = getList();
    var meta = getMeta();
    if (list.indexOf(key) === -1) list.unshift(key);
    if (!meta[key]) meta[key] = { t: Date.now() };
    if (info && info.price !== undefined && info.price !== '' && meta[key].p === undefined) {
      var price = parseInt(info.price, 10);
      if (!isNaN(price)) meta[key].p = price;
    }
    // The system as configured when it was saved (see configFor below).
    if (config) meta[key].c = config;
    saveMeta(meta);
    saveList(list, key, 'add');
    return key;
  }

  function remove(key) {
    var list = getList();
    var index = list.indexOf(key);
    if (index === -1) return;
    list.splice(index, 1);
    saveList(list, key, 'remove');
  }

  /** Puts a removed system back where it was, with its original saved date. */
  function restore(key, position, metaEntry) {
    restoreAll([{ key: key, position: position, meta: metaEntry }]);
  }

  /** The same for several entries at once: [{ key, position, meta }]. */
  function restoreAll(entries) {
    var list = getList();
    var meta = getMeta();
    var restored = [];
    entries.slice().sort(function (a, b) { return a.position - b.position; }).forEach(function (entry) {
      if (list.indexOf(entry.key) !== -1) return;
      list.splice(Math.min(entry.position, list.length), 0, entry.key);
      meta[entry.key] = entry.meta || { t: Date.now() };
      restored.push(entry.key);
    });
    if (!restored.length) return;
    saveMeta(meta);
    if (restored.length === 1) saveList(list, restored[0], 'add');
    else saveList(list, null, 'set');
  }

  function clear() {
    saveMeta({});
    saveList([], null, 'clear');
  }

  /* ---- hearts and counters ---------------------------------------------- */

  function titleFor(button) {
    return button.getAttribute('data-product-title') || 'this system';
  }

  // A heart on a system's own page stands for the build as it is set up
  // right now; anywhere else (collection cards, related products) it stands
  // for the system, and shows as saved when any build of it is.
  function paintButton(button, list) {
    var handle = button.getAttribute('data-product-handle');
    var config = configFor(handle);
    var saved = config ? list.indexOf(keyFor(handle, config)) !== -1 : entriesFor(handle, list).length > 0;
    button.classList.toggle(ACTIVE_CLASS, saved);
    button.classList.remove('adding');
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    if (!button.hasAttribute('role')) button.setAttribute('role', 'button');
    if (!button.hasAttribute('href') && !button.hasAttribute('tabindex')) button.setAttribute('tabindex', '0');
    button.setAttribute('aria-label', saved
      ? 'Remove ' + titleFor(button) + ' from your wishlist'
      : 'Save ' + titleFor(button) + ' to your wishlist');
  }

  function paintCounters(list) {
    var count = list.length;
    var bubbles = document.querySelectorAll('.dtxc-wishlist-count, .mobile-dtxc-wishlist-count, [data-wishlist-count]');
    Array.prototype.forEach.call(bubbles, function (bubble) {
      var span = bubble.matches('[data-wishlist-count]') ? bubble : bubble.querySelector('span');
      if (span) span.textContent = count;
      bubble.setAttribute('count', count);
      bubble.classList.toggle('count-zero', count === 0);
      bubble.classList.toggle('count-exist', count > 0);
      if (!bubble.matches('[data-wishlist-count]')) bubble.style.display = count ? '' : 'none';
    });
  }

  function refresh() {
    var list = getList();
    Array.prototype.forEach.call(document.querySelectorAll(BUTTON_SELECTOR), function (button) {
      paintButton(button, list);
    });
    paintCounters(list);
  }

  /**
   * The system exactly as the customer has set it up on its own product
   * page — variant, chosen options (with the options app's hidden pricing
   * tag), build total, case picture — so the wishlist can show it and add
   * that configuration to the basket rather than the bare product.
   *
   * Supplied by the product page (snippets/wattec-floating-atc.liquid sets
   * window.WattecWishlistCapture); anywhere else, or for another product,
   * there is no configuration and the plain product is saved as before.
   */
  function configFor(handle) {
    if (typeof window.WattecWishlistCapture !== 'function') return null;
    try { return window.WattecWishlistCapture(handle) || null; } catch (e) { return null; }
  }

  function toggleFrom(button) {
    var handle = button.getAttribute('data-product-handle');
    if (!handle) return;
    button.classList.remove('wwl-pop');
    void button.offsetWidth; // restart the little "pop" animation
    button.classList.add('wwl-pop');

    var title = titleFor(button);
    var named = title !== 'this system';
    var config = configFor(handle);
    var list = getList();
    var meta = getMeta();
    // On the system's own page: just this build. Elsewhere: every saved
    // build of the system (Undo puts them all back).
    var targets = config
      ? [keyFor(handle, config)].filter(function (key) { return list.indexOf(key) !== -1; })
      : entriesFor(handle, list);

    if (targets.length) {
      var removed = targets.map(function (key) {
        return { key: key, position: list.indexOf(key), meta: meta[key] };
      });
      if (targets.length === 1) {
        remove(targets[0]);
      } else {
        saveList(list.filter(function (key) { return targets.indexOf(key) === -1; }), null, 'set');
      }
      var what = targets.length > 1
        ? targets.length + ' builds of ' + (named ? title : 'this system')
        : (named ? title : 'System');
      toast({
        text: what + ' removed from your wishlist',
        actionLabel: 'Undo',
        action: function () { restoreAll(removed); }
      });
    } else {
      var others = entriesFor(handle, list).length;
      add(handle, { price: button.getAttribute('data-product-price'), config: config });
      toast({
        text: others && config
          ? 'This build of ' + (named ? title : 'the system') + ' saved to your wishlist too'
          : (named ? title : 'System') + ' saved to your wishlist',
        actionLabel: 'View wishlist',
        href: WISHLIST_URL,
        saved: true
      });
    }
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest(BUTTON_SELECTOR);
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    toggleFrom(button);
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    var button = event.target.closest && event.target.closest(BUTTON_SELECTOR);
    if (!button || (event.key === 'Enter' && button.hasAttribute('href'))) return;
    event.preventDefault();
    toggleFrom(button);
  });

  /* ---- toast ------------------------------------------------------------ */

  var toastEl = null;
  var toastTimer = null;

  function toast(options) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wwl-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    clearTimeout(toastTimer);
    toastEl.innerHTML = '';

    var icon = document.createElement('span');
    icon.className = 'wwl-toast__icon' + (options.saved ? ' is-saved' : '');
    icon.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-9.3-9.2C1.4 8 3.4 4.5 6.9 4.5c2.1 0 3.6 1.2 5.1 3 1.5-1.8 3-3 5.1-3 3.5 0 5.5 3.5 4.2 6.8-1.8 4.6-9.3 9.2-9.3 9.2z"/></svg>';
    toastEl.appendChild(icon);

    var text = document.createElement('span');
    text.className = 'wwl-toast__text';
    text.textContent = options.text;
    toastEl.appendChild(text);

    if (options.actionLabel) {
      var onWishlistPage = options.href && window.location.pathname.replace(/\/$/, '') === options.href;
      if (!onWishlistPage) {
        var action = document.createElement(options.href ? 'a' : 'button');
        action.className = 'wwl-toast__action';
        action.textContent = options.actionLabel;
        if (options.href) action.href = options.href;
        else {
          action.type = 'button';
          action.addEventListener('click', function () {
            options.action();
            hideToast();
          });
        }
        toastEl.appendChild(action);
      }
    }

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'wwl-toast__close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    close.addEventListener('click', hideToast);
    toastEl.appendChild(close);

    requestAnimationFrame(function () { toastEl.classList.add('is-visible'); });
    toastTimer = setTimeout(hideToast, options.duration || 5000);
    toastEl.onmouseenter = function () { clearTimeout(toastTimer); };
    toastEl.onmouseleave = function () { toastTimer = setTimeout(hideToast, 2500); };
  }

  function hideToast() {
    clearTimeout(toastTimer);
    if (toastEl) toastEl.classList.remove('is-visible');
  }

  /* ---- keeping up with new content and other tabs ----------------------- */

  var pending = false;
  function scheduleRefresh() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      refresh();
    });
  }

  function start() {
    refresh();
    if ('MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType === 1 && (node.matches(BUTTON_SELECTOR) || node.querySelector(BUTTON_SELECTOR))) {
              scheduleRefresh();
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  window.addEventListener('storage', function (event) {
    if (event.key !== LIST_KEY && event.key !== null) return;
    refresh();
    announce(getList(), null, 'sync');
  });

  // Returning with the browser's Back button can show a cached copy of the
  // page from before the customer changed their list somewhere else.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refresh();
  });

  // Systems saved exactly as configured before builds were told apart were
  // kept under the product alone. Each is given its build's own entry, so it
  // matches that build on its page and leaves room for other builds.
  function upgradeSavedBuilds() {
    var list = getList();
    var meta = getMeta();
    var upgraded = [];
    var changed = false;
    list.forEach(function (key) {
      var entry = meta[key];
      var newKey = key;
      if (key.indexOf(KEY_JOIN) === -1 && entry && entry.c) {
        newKey = keyFor(key, entry.c);
        if (newKey !== key) {
          delete meta[key];
          meta[newKey] = entry;
          changed = true;
        }
      }
      if (upgraded.indexOf(newKey) === -1) upgraded.push(newKey);
    });
    if (!changed) return;
    saveMeta(meta);
    write(LIST_KEY, upgraded.join(DELIMITER));
  }
  upgradeSavedBuilds();

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.WattecWishlist = {
    url: WISHLIST_URL,
    get: getList,
    getMeta: getMeta,
    has: has,
    hasProduct: hasProduct,
    entriesFor: entriesFor,
    productOf: productOf,
    keyFor: keyFor,
    add: add,
    remove: remove,
    restore: restore,
    restoreAll: restoreAll,
    clear: clear,
    set: function (list) { saveList(list, null, 'set'); },
    setMeta: saveMeta,
    toast: toast,
    refresh: refresh
  };

  // Names the theme's older code may still call.
  window.getWishlist = getList;
  window.wishlistContains = hasProduct;
  window.wishListCount = refresh;
})();
